import { useFragment } from '@apollo/client';
import { useState } from 'react';
import {
  AccountSettingsHeaderFieldsFragmentDoc,
  type GQLAccountSettingsHeaderFieldsFragment,
} from 'src/components/AccountSettingsHeader/AccountSettingsHeader.generated';
import {
  GQLIdentityTeamStatus,
  type GQLUpdateIdentityUserAvatarInput,
} from 'src/generated/graph-api';
import { useUpdateUserAvatar } from 'src/mutations/avatar/updateUserAvatarMutation';
import { useQueryParams } from '@/utils/use-query-params';

export const useAccountSettingsLogic = (userId: string) => {
  const { addQueryParam, removeQueryParam } = useQueryParams();

  const { data: user } = useFragment<GQLAccountSettingsHeaderFieldsFragment>({
    fragment: AccountSettingsHeaderFieldsFragmentDoc,
    fragmentName: 'AccountSettingsHeaderFields',
    from: { id: userId, __typename: 'IdentityUser' },
  });

  const [updateAvatarMutation, { loading: loadingUpdateAvatar }] =
    useUpdateUserAvatar(userId);
  const [avatarEditModalOpen, setAvatarEditModalOpen] = useState(false);

  const name =
    user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`;
  const teams =
    user?.teamMemberships?.flatMap((membership) =>
      membership?.team &&
      membership.status === 'ACTIVE' &&
      membership.team.status === GQLIdentityTeamStatus.ACTIVE
        ? membership.team
        : [],
    ) || [];

  const handleTeamChange = (teamId: string) => {
    if (teamId) {
      addQueryParam('team', teamId);
    } else {
      removeQueryParam('team');
    }
  };

  const onSubmitAvatar = (input: GQLUpdateIdentityUserAvatarInput) =>
    updateAvatarMutation(input).then(() => setAvatarEditModalOpen(false));

  return {
    user,
    name,
    teams,
    avatarEditModalOpen,
    setAvatarEditModalOpen,
    loadingUpdateAvatar,
    handleTeamChange,
    onSubmitAvatar,
  };
};
